import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { debounce } from "@mui/material";
import { CustomDialog } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import DocumentAutoCompleteSearch from "../../../../../../common/components/autocomplete-text-field/document-autocomplete-search/DocumentAutocompleteSearch";
import {
	ContentAutocompleteSearch,
	ContentToLink,
} from "../../../../../my-review/components/right-panels/options-panel/tabs/link-tab/components";
import { useApi, useTrackEvent } from "../../../../../../common/hooks";
import { AiDetectionsService, ProjectDocumentService, SearchCardService } from "../../../../../../api";
import { OPERATORS } from "../../../../../../common/constants";
import styles from "./AssociateContentDIalog.module.css";
import { isNonEmptyArray } from "../../../../../../common/utils";

const page = 0;
const limit = 20;
export const AssociateContentDialog = ({ open, onClose, fieldUuid, onCheckSelectedContent, onSubmit }) => {
	const projectId = useSelector(({ context }) => context?.project?.id);
	const sort = useSelector(({ mySearch }) => mySearch.selectedSortAlgo);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [isLoadingContents, setIsLoadingContents] = useState(false);
	const [searchDocument, setSearchDocument] = useState([]);
	const [searchContents, setSearchContents] = useState({});
	const [searchDocumentInputValue, setSearchDocumentInputValue] = useState("");
	const [searchContentInputValue, setSearchContentInputValue] = useState("");
	const [selectedDocument, setSelectedDocument] = useState(null);
	const [selectedContents, setSelectedContents] = useState([]);
	const [searchDebouncedDocument, setSearchDebouncedDocument] = useState("");
	const [searchDebouncedContent, setSearchDebouncedContent] = useState("");
	const [resetKey, setResetKey] = useState(1);
	const { call: searchDocs } = useApi(ProjectDocumentService.search);
	const { call: searchContent } = useApi(SearchCardService.search);
	const { call: addNewValueSource } = useApi(AiDetectionsService.addNewValueSource);
	const trackEvent = useTrackEvent();
	const optionText = translate("dashboards.project-sheet.section-details.associate-content.helper").split(" – ");
	const generateSearchResultLabel = () => {
		if (searchDebouncedContent === searchContentInputValue) {
			if (searchDebouncedContent.length < 3) {
				return translate(
					"dashboards.project-sheet.section-details.associate-content.select-content.not-completed"
				);
			}
			if (searchDebouncedContent.length >= 3 && searchContents?.resultCount === 0) {
				return translate("dashboards.project-sheet.section-details.associate-content.select-content.not-found");
			}
		}
		return "";
	};
	useEffect(() => {
		if (!open) {
			setSearchDocumentInputValue("");
			setSearchContentInputValue("");
			setSelectedContents([]);
			setSelectedDocument(null);
			setSearchDocument([]);
			setSearchContents({});
		}
	}, [open]);
	/** Document Search */
	useEffect(() => {
		if (
			searchDebouncedDocument &&
			searchDebouncedDocument === searchDocumentInputValue &&
			searchDebouncedDocument.length > 1
		) {
			searchDocs({ projectId }, { name: searchDebouncedDocument })
				.then((data) => setSearchDocument(data))
				.catch(console.error)
				.finally(() => setIsLoadingDocuments(false));
		}
	}, [projectId, searchDocs, searchDebouncedDocument, searchDocumentInputValue]);
	const debouncedSearch = debounce((name) => {
		setSearchDebouncedDocument(name);
	}, 500);
	const handleSearchDocument = (name) => {
		setSearchDocumentInputValue(name);
		if (!name) {
			setSearchDocument([]);
			setIsLoadingDocuments(false);
			return;
		}
		if (name.length > 1) {
			setIsLoadingDocuments(true);
			debouncedSearch(name);
		}
	};
	const handleDocumentSelection = ({ documentId }) => {
		setSelectedDocument(documentId);
	};
	const handleResetDocument = () => {
		setSelectedDocument(null);
	};
	/** Content Search */
	useEffect(() => {
		if (searchDebouncedContent === searchContentInputValue) {
			if (searchDebouncedContent && searchDebouncedContent.length > 2) {
				const filters = {
					separator: OPERATORS.AND,
					Type: "InformationSearchFilterDTO",
					isFilterTocOut: false,
					keywordFilters: {
						filters: [{ keywords: [searchDebouncedContent], operator: "IS_EXACTLY", matchAll: true }],
					},
				};
				const regex = /^(req_|info_)(\d+)$/i;
				const match = searchDebouncedContent.match(regex);
				if (match) {
					const [, , numbers] = match;
					filters.informationIds = { elements: [Number.parseInt(numbers, 10)] };
					delete filters.keywordFilters;
				}
				if (selectedDocument) {
					filters.documents = { is: true, matchAll: true, elements: [selectedDocument] };
				}
				searchContent({ projectId }, filters, page, limit, sort.trim()?.length === 0 ? null : sort)
					.then((data) => setSearchContents(data))
					.catch(console.error)
					.finally(() => setIsLoadingContents(false));
			} else {
				setSearchContents({});
				setIsLoadingContents(false);
			}
		}
	}, [projectId, searchContent, searchDebouncedContent, searchContentInputValue, selectedDocument, sort]);
	const debouncedSearchContents = debounce((name) => {
		setSearchDebouncedContent(name);
	}, 500);
	const handleSearchContent = (name) => {
		setSearchContentInputValue(name);
		setIsLoadingContents(true);
		debouncedSearchContents(name);
	};
	const handleContentSelection = (content) => {
		if (!selectedContents.some((selectedContent) => selectedContent.informationId === content.informationId)) {
			setSelectedContents((prev) => [...prev, content]);
		}
		setResetKey((prev) => prev + 1);
		setIsLoadingContents(false);
		setSearchContentInputValue("");
		setSearchContents({});
	};
	const handleResetContent = (selectedContent) => {
		setSelectedContents((prev) =>
			prev.filter((content) => content.informationId !== selectedContent.informationId)
		);
	};
	const handleSubmit = () => {
		if (projectId && fieldUuid && isNonEmptyArray(selectedContents)) {
			addNewValueSource(
				{ projectId, fieldUuid },
				selectedContents.map((content) => content.informationId)
			)
				.then(() => {
					trackEvent("Detection_Adds_content");
					onSubmit();
					onClose();
				})
				.catch(console.error);
		}
	};
	const handleResetAutoContent = () => {
		setSearchContentInputValue("");
		setSearchContents({});
		setSearchDebouncedContent("");
		setIsLoadingContents(false);
	};
	return (
		<CustomDialog
			className={styles.associateContent}
			classNameDialog={styles.associateContentDialog}
			open={open}
			submitDisabled={!isNonEmptyArray(selectedContents) || !fieldUuid}
			submitLabel={translate("common:btn.validate")}
			title={translate("dashboards.project-sheet.section-details.new-content")}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.block}>
				<div>
					<span className={styles.title}>{optionText[0]}</span>
					<span> - {optionText[1]}</span>
				</div>
				<div>
					<DocumentAutoCompleteSearch
						documents={searchDocument?.contents || []}
						emptyStateLabel={translate("common:document-autocomplete-search.empty-state")}
						error={false}
						inputValue={searchDocumentInputValue}
						isLoading={isLoadingDocuments}
						label={translate("dashboards.project-sheet.section-details.associate-content.select-document")}
						onReset={handleResetDocument}
						onSearch={handleSearchDocument}
						onSelect={handleDocumentSelection}
					/>
				</div>
			</div>
			<div className={styles.block}>
				<div>
					<span className={styles.title}>
						{translate("dashboards.project-sheet.section-details.associate-content.title")}
					</span>
					<span className={styles.star}>*</span>
				</div>
				<div>
					<ContentAutocompleteSearch
						clearOnSelect
						contents={searchContents?.details?.contents || []}
						emptyStateLabel={generateSearchResultLabel()}
						error={false}
						informationText={translate(
							"dashboards.project-sheet.section-details.associate-content.already-associated"
						)}
						inputValue={searchContentInputValue}
						isLoading={isLoadingContents}
						label={translate("dashboards.project-sheet.section-details.associate-content.select-content")}
						listBoxClassName={styles.listBox}
						resetKey={resetKey}
						selectedClassName={styles.selectedRow}
						selectedItemClassName={styles.selectedRowContent}
						onCheckSelectedContent={onCheckSelectedContent}
						onReset={handleResetAutoContent}
						onSearch={handleSearchContent}
						onSelect={handleContentSelection}
					/>
				</div>
				{isNonEmptyArray(selectedContents) &&
					selectedContents.map((selectedContent) => (
						<div className={styles.contentLink}>
							<ContentToLink
								content={selectedContent}
								onClose={() => handleResetContent(selectedContent)}
							/>
						</div>
					))}
			</div>
		</CustomDialog>
	);
};
